import React from 'react';
import { arrayOf, array, object, shape, string, func } from 'prop-types';
import { FormattedMessage } from '../../../../util/reactIntl';
import { InlineTextButton } from '../../../../components';
import classNames from 'classnames';

import css from './CategoryBreadcrumb.module.css';

/**
 * Recursively finds category names from the listingCategories
 * based on selected category IDs in urlQueryParams.
 *
 * @param {Array} categories - The full category hierarchy.
 * @param {Object} urlQueryParams - The selected category IDs from the URL.
 * @returns {Array} - Ordered breadcrumb items.
 */
const getBreadcrumbItems = (categories, urlQueryParams) => {
  const breadcrumb = [];
  let currentLevelCategories = categories;

  for (let i = 1; i <= 4; i++) {
    const categoryKey = `pub_categoryLevel${i}`;
    const selectedCategoryId = urlQueryParams[categoryKey];

    if (!selectedCategoryId) break;

    const selectedCategory = currentLevelCategories.find(cat => cat.id === selectedCategoryId);
    if (selectedCategory) {
      breadcrumb.push({ id: selectedCategory.id, name: selectedCategory.name, level: i });
      currentLevelCategories = selectedCategory.subcategories || [];
    } else {
      break;
    }
  }

  return breadcrumb;
};

/**
 * Generates search parameters for category breadcrumb navigation.
 * - Keeps all non-category filters unchanged.
 * - Adjusts category levels up to the selected one, setting deeper levels to null.
 * - When navigating "Home," all categories are reset to null.
 *
 * @param {Object} urlQueryParams - The existing search filters.
 * @param {number|null} level - The category level to navigate to (null for Home).
 * @returns {Object} - The updated search query parameters.
 */
const getUpdatedSearchParams = (urlQueryParams, level) => {
  const updatedCategories = {};
  for (let i = 1; i <= 4; i++) {
    const key = `pub_categoryLevel${i}`;
    updatedCategories[key] =
      level === null ? null : i <= level ? urlQueryParams[key] || null : null;
  }

  return updatedCategories;
};

const CategoryBreadcrumb = props => {
  const { rootClassName, className, listingCategories, urlQueryParams, onSubmit } = props;
  const breadcrumbItems = getBreadcrumbItems(listingCategories, urlQueryParams);

  const classes = classNames(rootClassName || css.root, className);
  return (
    <nav className={classes}>
      <ul className={css.breadcrumbList}>
        <li className={css.breadcrumbItem}>
          <InlineTextButton
            type="button"
            className={css.breadcrumbButton}
            onClick={() => onSubmit(getUpdatedSearchParams(urlQueryParams, null))}
          >
            <FormattedMessage id="SearchPage.MainPanelHeader.CategoryBreadcrumb.home" />
          </InlineTextButton>
        </li>
        {breadcrumbItems.map(item => (
          <li className={css.breadcrumbItem} key={item.id}>
            <InlineTextButton
              type="button"
              className={css.breadcrumbButton}
              onClick={() => onSubmit(getUpdatedSearchParams(urlQueryParams, item.level))}
            >
              {item.name}
            </InlineTextButton>
          </li>
        ))}
      </ul>
    </nav>
  );
};

CategoryBreadcrumb.defaultProps = {
  rootClassName: null,
  className: null,
};

CategoryBreadcrumb.propTypes = {
  rootClassName: string,
  className: string,
  listingCategories: arrayOf(
    shape({
      id: string.isRequired,
      name: string.isRequired,
      subcategories: array,
    })
  ).isRequired,
  urlQueryParams: object.isRequired,
  onSubmit: func.isRequired,
};

export default CategoryBreadcrumb;
